import {useNavigate} from 'react-router-dom';

import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext';
import {LoggedInAuth} from 'src/hooks/useAuth';
import {ApiOrganization, ApiUserProfile} from 'src/lib/api/auth';
import {makeUuidPrefix} from 'src/lib/routeUtils';
import * as tw from 'src/lib/tailwindClasses';

type Props = {
  auth: LoggedInAuth;
  currentOrganization: ApiOrganization;
  profileOrganizations: ApiOrganization[];
  profile: ApiUserProfile;
};

export default function OrganizationSwitch({
  auth,
  currentOrganization,
  profileOrganizations,
  profile,
}: Props) {
  const {t} = useTranslations();
  const parentProfileOrganizations = profileOrganizations
    .filter((org) => !!org.childOrganizations.length)
    .sort(
      (a, b) =>
        (b.id === profile.organizationKey ? 1 : 0) - (a.id === profile.organizationKey ? 1 : 0)
    ); // sort the home org to the top

  const orphanProfileOrganizations = profileOrganizations.filter(
    (org) => org.childOrganizations.length === 0
  );
  return (
    <PageContainer>
      <PageTitle
        pageStack={[
          {
            title: t('organizations'),
            to: '/organizations',
          },
        ]}
      />

      {parentProfileOrganizations.map((profileOrganization) => (
        <ul key={profileOrganization.id}>
          <div className="font-bold mt-2 mb-4">
            <OrgListItem org={profileOrganization} auth={auth} />
          </div>
          <div className="ml-4">
            {profileOrganization.childOrganizations
              .filter((o) => o.products.includes('HydroForecast'))
              .map((childOrg, i) => (
                <OrgListItem key={i} org={childOrg} auth={auth} />
              ))}
          </div>
        </ul>
      ))}

      {!!orphanProfileOrganizations.length && (
        <div>
          <ul>
            {orphanProfileOrganizations
              .filter((o) => o.products.includes('HydroForecast'))
              .map((org, i) => (
                <OrgListItem key={i} org={org} auth={auth} />
              ))}
          </ul>
        </div>
      )}
    </PageContainer>
  );
}

function OrgListItem({org, auth}: {org: ApiOrganization; auth: LoggedInAuth}) {
  const navigate = useNavigate();
  return (
    <li
      key={org.id}
      className={`${tw.link} cursor-pointer w-fit`}
      onClick={() => {
        // Load-bearing: it points the API context at the new org before the
        // project list's queries fire. Safe to pair with navigate() here
        // since this page has no query-param effects to race (see the
        // setCurrentOrganization doc in useAuth.ts).
        auth.setCurrentOrganization(org);
        navigate(`/${makeUuidPrefix(org.id)}/projects`);
      }}
    >
      {org.name}
    </li>
  );
}
